import { Box } from "@chakra-ui/react";
import CDS from "design-system";
import moment from "moment-timezone";
import { CalendarEvent } from "../types";

interface EventDescriptionProps {
  event: CalendarEvent;
  timezone: string;
  color?: string;
}

export default function EventDescription({
  event,
  timezone,
  color = "white",
}: EventDescriptionProps) {
  const MARGIN_PX = "4px";
  const startMoment = moment.tz(event.start, timezone);
  const endMoment = moment.tz(event.end, timezone);
  const durationMinutes = endMoment.diff(startMoment, "minutes");

  const formatTime = (includeEndTime = true): string => {
    if (!includeEndTime || durationMinutes < 30) {
      return startMoment.format("h:mma");
    }

    // For longer events, show end time
    const startTimeString = startMoment.format(
      startMoment.format("A") === endMoment.format("A") ? "h:mm" : "h:mma",
    );
    const endTimeString = endMoment.format("h:mma");

    return `${startTimeString} - ${endTimeString}`;
  };

  const timeText = formatTime(durationMinutes >= 30);

  // For 15-minute events, concatenate title and time with comma
  if (durationMinutes <= 30) {
    return (
      <CDS.Text14
        fontSize={durationMinutes <= 15 ? "3xs" : "2xs"}
        color={color}
        m={`0 0 0 ${MARGIN_PX}`}
        width={`calc(100% - ${MARGIN_PX})`}
        maxWidth={`calc(100% - ${MARGIN_PX})`}
        boxSizing="border-box"
        overflow="hidden"
        textOverflow="clip"
        whiteSpace="nowrap"
        lineHeight="1.1"
      >
        <CDS.Text10 as="span" fontSize="inherit" lineHeight="inherit" fontWeight="bold">{event.title}</CDS.Text10>
        <CDS.Text10 as="span" fontSize="inherit" lineHeight="inherit" fontWeight="normal">
          ,
          {" "}
          {timeText}
        </CDS.Text10>
      </CDS.Text14>
    );
  }

  // For 30+ minute events, show title and time as separate elements
  return (
    <Box
      m={`0 0 2px ${MARGIN_PX}`}
      width={`calc(100% - ${MARGIN_PX})`}
      maxWidth={`calc(100% - ${MARGIN_PX})`}
      height="100%"
      maxHeight="100%"
      boxSizing="border-box"
      overflow="hidden"
      py="2px"
    >
      <CDS.Text10
        fontSize="2xs"
        fontWeight="bold"
        color={color}
        overflow="hidden"
        textOverflow="clip"
        whiteSpace={durationMinutes >= 75 ? "normal" : "nowrap"}
        wordBreak={durationMinutes >= 75 ? "break-word" : "normal"}
        noOfLines={durationMinutes >= 75 ? 2 : 1}
        lineHeight="1.1"
      >
        {event.title}
      </CDS.Text10>
      <CDS.Text10
        fontSize="2xs"
        fontWeight="normal"
        color={color}
        overflow="hidden"
        textOverflow="clip"
        whiteSpace="nowrap"
        mt="2px"
        lineHeight="1.1"
      >
        {timeText}
      </CDS.Text10>
    </Box>
  );
}
